import { toast } from "sonner";
import type { AbrangenciaDoServico } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";

export type DadosDoServico = {
  label: string;
  group: string;
  abrangencia: AbrangenciaDoServico;
  descricao: string;
  materialOptionIds: string[];
  escalasPorMaterial: Record<string, string[]>;
  /** undefined = a foto não mudou; null = tirar. */
  imagem: string | null | undefined;
};

/** Gravar e excluir o serviço do Catálogo, com tudo que lê o serviço invalidado junto (a lista, o
 *  pedido da clínica, as cores, a visualização do catálogo e a foto). */
export function useSalvarServico() {
  const utils = trpc.useUtils();
  const create = trpc.prosthesis.catalog.createCustomServiceCategory.useMutation();
  const update = trpc.prosthesis.catalog.updateCustomServiceCategory.useMutation();
  const salvarImagem = trpc.prosthesis.catalog.salvarImagemDoServico.useMutation();
  const excluir = trpc.prosthesis.catalog.deleteCustomServiceCategory.useMutation();

  const invalidar = () => {
    utils.prosthesis.catalog.myServiceCategories.invalidate();
    utils.prosthesis.catalog.serviceCategories.invalidate();
    utils.prosthesis.catalog.myShadeOptions.invalidate();
    utils.prosthesis.catalog.shadeOptions.invalidate();
    utils.prosthesis.catalog.imagemDoServico.invalidate();
    utils.catalogoPublico.visualizar.invalidate();
    utils.fichaTecnica.resumo.invalidate();
  };

  async function salvar(id: string | null, d: DadosDoServico): Promise<boolean> {
    try {
      // Só manda a cor dos materiais que este serviço oferece: mexer na escala e depois
      // desmarcar o material não pode gravar uma declaração fantasma.
      const escalasPorMaterial = Object.fromEntries(Object.entries(d.escalasPorMaterial).filter(([m]) => d.materialOptionIds.includes(m)));
      const comum = {
        label: d.label.trim(),
        group: d.group,
        abrangencia: d.abrangencia,
        descricao: d.descricao.trim() || null,
        materialOptionIds: d.materialOptionIds,
        escalasPorMaterial,
      };
      const serviceCategoryId = id ?? (await create.mutateAsync(comum)).id;
      if (id) await update.mutateAsync({ serviceCategoryId, ...comum });
      if (d.imagem !== undefined) await salvarImagem.mutateAsync({ serviceCategoryId, dataUrl: d.imagem });
      toast.success(id ? "Serviço atualizado." : "Serviço adicionado.");
      return true;
    } catch (e) {
      toast.error((e as Error).message || "Erro ao salvar serviço.");
      return false;
    } finally {
      invalidar();
    }
  }

  async function remover(id: string): Promise<boolean> {
    try {
      await excluir.mutateAsync({ serviceCategoryId: id });
      toast.success("Serviço excluído.");
      invalidar();
      return true;
    } catch (e) {
      toast.error((e as Error).message || "Erro ao excluir serviço.");
      return false;
    }
  }

  return {
    salvar,
    remover,
    salvando: create.isPending || update.isPending || salvarImagem.isPending,
    excluindo: excluir.isPending,
  };
}
